import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveDynamicComponent as _resolveDynamicComponent, normalizeClass as _normalizeClass, createBlock as _createBlock, unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "file-info" }
const _hoisted_2 = {
  key: 0,
  class: "preview__button"
}
const _hoisted_3 = ["src", "alt"]
const _hoisted_4 = {
  key: 3,
  class: "preview__error-text"
}
const _hoisted_5 = {
  key: 4,
  class: "preview__loading-spinner"
}
const _hoisted_6 = { class: "info" }
const _hoisted_7 = { key: 0 }
const _hoisted_8 = { key: 1 }
const _hoisted_9 = { key: 2 }
const _hoisted_10 = {
  key: 1,
  class: "info"
}
const _hoisted_11 = { class: "info__heading" }
const _hoisted_12 = { key: 0 }
const _hoisted_13 = { key: 1 }
const _hoisted_14 = { key: 2 }
const _hoisted_15 = { key: 3 }
const _hoisted_16 = { key: 4 }
const _hoisted_17 = { key: 5 }
const _hoisted_18 = { key: 6 }
const _hoisted_19 = { key: 7 }
const _hoisted_20 = { key: 8 }
const _hoisted_21 = { key: 9 }
const _hoisted_22 = { key: 10 }
const _hoisted_23 = { key: 11 }
const _hoisted_24 = { class: "actions" }

import { computed, ref, watch } from 'vue'
import { useGeneralStore } from '@/pinia'
import { useCoreEntityStore } from '@/pinia/coreEntity'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { formatNumber } from '@/utils/math'
import { Attachment } from '@/services/attachments.service'
import { getAttachmentFileBlob } from '@/services/files.service'
import BadgeSquare from '@/design-system/atoms/BadgeSquare.vue'
import ButtonOutline from '@/design-system/atoms/buttons/ButtonOutline.vue'
import IconExternalLink from '@/design-system/assets/icons/IconExternalLink.vue'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import pdfvuer from 'pdfvuer/src/Pdfvuer.vue'
import TooltipStandardV2 from '@/design-system/atoms/TooltipStandardV2.vue'
import { getLineLayerById } from '@/services/lines.service'
import { getZoneLayerById } from '@/services/zones.service'
import { getPoiById } from '@/services/pois.service'
import { getRealmId } from '@/utils/realmId'
import { getActCategoryById } from '@/services/acts.service'


export default /*@__PURE__*/_defineComponent({
  __name: 'FileInfo',
  emits: ['archiveFile', 'deleteFile', 'downloadFile'],
  setup(__props, { emit: __emit }) {

const piniaStore = useGeneralStore()
const coreEntityStore = useCoreEntityStore()
const store = useStore()
const { t } = useI18n()

const emits = __emit
const selectedFile = computed(() => {
  return piniaStore.selectedFile
})

const actCategoryName = ref('')
const actTypeName = ref('')
const actStatusName = ref('')
const coreEntityTypes = ['COMPLEX', 'REAL_ESTATE', 'CONSTRUCTION', 'LEVEL', 'SPACE']
const fileBlobSource = ref('')
const fileType = ref<string | undefined>('')
const loadingFile = ref(false)
const parentNames = ref<Record<string, string>>({})
const previewHovered = ref(false)
const showPreviewFailedText = ref(false)

const documentMetadata = computed(() => selectedFile.value?.metadata?.documentMetadata)
const imageMetadata = computed(() => selectedFile.value?.metadata?.imageMetadata)

function checkValidFileType(item: Attachment) {
  return (
    item.fileType === 'gif' ||
    item.fileType === 'jpg' ||
    item.fileType === 'jpeg' ||
    item.fileType === 'pdf' ||
    item.fileType === 'png' ||
    item.fileType === 'svg'
  )
}

async function getEntityParents(file: Attachment): Promise<string | undefined> {
  try {
    if (!file.entityId) return undefined
    let parents
    if (coreEntityTypes.includes(file.entityType)) {
      const currentEntity = await coreEntityStore.getCoreEntity(file.entityId)
      const parentNames = await coreEntityStore.getParents(file.entityId)
      parents = [...parentNames, currentEntity]
    } else if (file.entityType === 'POI') {
      const poi = await getPoiById(file.entityId)
      if (!poi) return undefined
      const currentEntity = await coreEntityStore.getCoreEntity(poi.coreId)
      const parentNames = await coreEntityStore.getParents(poi.coreId)
      parents = [...parentNames, currentEntity, poi]
    } else if (file.entityType === 'ZONE') {
      const zone = await getZoneLayerById(file.entityId)
      if (!zone) return undefined
      const currentEntity = await coreEntityStore.getCoreEntity(zone.coreId)
      const parentNames = await coreEntityStore.getParents(zone.coreId)
      parents = [...parentNames, currentEntity, zone]
    } else if (file.entityType === 'LINE') {
      const line = await getLineLayerById(file.entityId)
      if (!line) return undefined
      const currentEntity = await coreEntityStore.getCoreEntity(line.coreId)
      const parentNames = await coreEntityStore.getParents(line.coreId)
      parents = [...parentNames, currentEntity, line]
    } else {
      // case realm
      parents = [{ name: getRealmId() }]
    }
    if (!parents) return undefined
    const parentNames = [...parents].map((parent) => parent.name).join(', ')
    return parentNames
  } catch (error) {
    return undefined
  }
}
async function getPreviewFile() {
  if (!piniaStore.selectedFile) return
  try {
    fileType.value = piniaStore.selectedFile.fileType ?? ''
    loadingFile.value = true
    const res = await getAttachmentFileBlob(piniaStore.selectedFile)
    const blob =
      fileType?.value === 'svg'
        ? // @ts-ignore
          new Blob([res.data], { type: 'image/svg+xml' })
        : // @ts-ignore
          new Blob([res.data])
    fileBlobSource.value = window.URL.createObjectURL(blob)
    showPreviewFailedText.value = false
  } catch (e) {
    showPreviewFailedText.value = true
    console.error(e)
  } finally {
    loadingFile.value = false
  }
}
function previewFile() {
  if (showPreviewFailedText.value || loadingFile.value) return
  piniaStore.setCurrentOverlayModalPinia('previewFilePinia')
  piniaStore.setShowOverlayModalPinia(true)
}
function resetActData() {
  actCategoryName.value = ''
  actTypeName.value = ''
  actStatusName.value = ''
}
function setPreviewHovered(value: boolean) {
  if (showPreviewFailedText.value || loadingFile.value) previewHovered.value = false
  else previewHovered.value = value
}
watch(
  selectedFile,
  async (newFile) => {
    resetActData()
    if (newFile && checkValidFileType(newFile)) getPreviewFile()
    if (!newFile?.entityId) return
    const parents = await getEntityParents(newFile)
    if (!parents) return
    parentNames.value[newFile.entityId] = parents
    if (newFile.actCategory) {
      const actCategory = await getActCategoryById(newFile.actCategory)
      if (!actCategory) return
      actCategoryName.value = actCategory.name
      actTypeName.value = actCategory.types.find((type) => type.id === newFile?.actType)?.name ?? ''
      actStatusName.value =
        actCategory.statuses.find((status) => status.id === newFile?.actStatus)?.name ?? ''
    }
  },
  { immediate: true }
)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("main", _hoisted_1, [
    (selectedFile.value && checkValidFileType(selectedFile.value))
      ? (_openBlock(), _createElementBlock("section", {
          key: 0,
          class: "preview",
          onClick: previewFile,
          onMouseenter: _cache[0] || (_cache[0] = ($event: any) => (setPreviewHovered(true))),
          onMouseleave: _cache[1] || (_cache[1] = ($event: any) => (setPreviewHovered(false)))
        }, [
          (previewHovered.value && fileBlobSource.value && !showPreviewFailedText.value && !loadingFile.value)
            ? (_openBlock(), _createElementBlock("button", _hoisted_2, [
                _createVNode(IconExternalLink, { class: "preview__button__icon" })
              ]))
            : _createCommentVNode("", true),
          (fileBlobSource.value && !loadingFile.value && fileType.value === 'pdf')
            ? (_openBlock(), _createBlock(_resolveDynamicComponent(pdfvuer), {
                key: 1,
                class: _normalizeClass(["preview__pdf", { preview__pdf__hover: previewHovered.value }]),
                page: 1,
                src: fileBlobSource.value
              }, null, 8, ["class", "src"]))
            : _createCommentVNode("", true),
          (fileBlobSource.value && !loadingFile.value && fileType.value !== 'pdf')
            ? (_openBlock(), _createElementBlock("img", {
                key: 2,
                class: _normalizeClass(["preview__image", { preview__image__hover: previewHovered.value }]),
                src: fileBlobSource.value,
                alt: _unref(t)('general__text__preview-click-to')
              }, null, 10, _hoisted_3))
            : _createCommentVNode("", true),
          (showPreviewFailedText.value && !fileBlobSource.value && !loadingFile.value)
            ? (_openBlock(), _createElementBlock("p", _hoisted_4, _toDisplayString(_unref(t)('general__text__preview-failed')), 1))
            : _createCommentVNode("", true),
          (loadingFile.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                _createVNode(PulseLoader, {
                  color: "#3f46d6",
                  size: "16px"
                })
              ]))
            : _createCommentVNode("", true)
        ], 32))
      : _createCommentVNode("", true),
    _createElementVNode("section", _hoisted_6, [
      _createElementVNode("table", null, [
        (actCategoryName.value.length)
          ? (_openBlock(), _createElementBlock("tr", _hoisted_7, [
              _createElementVNode("td", null, _toDisplayString(_unref(t)('general__text__category')), 1),
              _createElementVNode("td", null, _toDisplayString(actCategoryName.value), 1)
            ]))
          : _createCommentVNode("", true),
        (actTypeName.value.length)
          ? (_openBlock(), _createElementBlock("tr", _hoisted_8, [
              _createElementVNode("td", null, _toDisplayString(_unref(t)('general__text__type')), 1),
              _createElementVNode("td", null, _toDisplayString(actTypeName.value), 1)
            ]))
          : _createCommentVNode("", true),
        (actStatusName.value.length)
          ? (_openBlock(), _createElementBlock("tr", _hoisted_9, [
              _createElementVNode("td", null, _toDisplayString(_unref(t)('general__text__status')), 1),
              _createElementVNode("td", null, _toDisplayString(actStatusName.value), 1)
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("tr", null, [
          _createElementVNode("td", null, _toDisplayString(_unref(t)('general__text__uploaded')), 1),
          _createElementVNode("td", null, _toDisplayString(selectedFile.value?.createdDate ? new Date(selectedFile.value.createdDate).toLocaleString() : ''), 1)
        ]),
        _createElementVNode("tr", null, [
          _createElementVNode("td", null, _toDisplayString(_unref(t)('general__text__uploaded-by')), 1),
          _createElementVNode("td", null, _toDisplayString(selectedFile.value?.uploadedBy ?? ''), 1)
        ]),
        _createElementVNode("tr", null, [
          _createElementVNode("td", null, _toDisplayString(_unref(t)('general__text__file-type')), 1),
          _createElementVNode("td", null, _toDisplayString(selectedFile.value?.fileType ?? ''), 1)
        ]),
        _createElementVNode("tr", null, [
          _createElementVNode("td", null, _toDisplayString(_unref(t)('general__text__size')), 1),
          _createElementVNode("td", null, _toDisplayString(selectedFile.value?.fileSize ? _unref(formatNumber)(selectedFile.value.fileSize / 1000, 0) + ' kB' : ''), 1)
        ]),
        _createElementVNode("tr", null, [
          _createElementVNode("td", null, _toDisplayString(_unref(t)('general__text__placement')), 1),
          (selectedFile.value?.entityId && parentNames.value[selectedFile.value?.entityId])
            ? (_openBlock(), _createBlock(BadgeSquare, {
                key: 0,
                badgeText: parentNames.value[selectedFile.value?.entityId],
                grey: true,
                size: "extra-small"
              }, null, 8, ["badgeText"]))
            : _createCommentVNode("", true)
        ])
      ])
    ]),
    _cache[5] || (_cache[5] = _createElementVNode("br", null, null, -1)),
    (documentMetadata.value || imageMetadata.value)
      ? (_openBlock(), _createElementBlock("section", _hoisted_10, [
          _createElementVNode("p", _hoisted_11, _toDisplayString(`${_unref(t)('general__text__additional')} ${_unref(t)('general__text__information').toLowerCase()}`), 1),
          _createElementVNode("table", null, [
            (documentMetadata.value?.lastModified)
              ? (_openBlock(), _createElementBlock("tr", _hoisted_12, [
                  _createElementVNode("td", null, _toDisplayString(_unref(t)('general__text__last-modified-date')), 1),
                  _createElementVNode("td", null, _toDisplayString(new Date(documentMetadata.value.lastModified).toLocaleString()), 1)
                ]))
              : _createCommentVNode("", true),
            (documentMetadata.value?.createdDate)
              ? (_openBlock(), _createElementBlock("tr", _hoisted_13, [
                  _createElementVNode("td", null, _toDisplayString(_unref(t)('general__text__created-date')), 1),
                  _createElementVNode("td", null, _toDisplayString(new Date(documentMetadata.value.createdDate).toLocaleString()), 1)
                ]))
              : _createCommentVNode("", true),
            (documentMetadata.value?.lastAuthor)
              ? (_openBlock(), _createElementBlock("tr", _hoisted_14, [
                  _createElementVNode("td", null, _toDisplayString(`${_unref(t)('general__text__last-modified')} ${_unref(t)('general__text__by').toLowerCase()}`), 1),
                  _createElementVNode("td", null, _toDisplayString(documentMetadata.value.lastAuthor), 1)
                ]))
              : _createCommentVNode("", true),
            (documentMetadata.value?.creator)
              ? (_openBlock(), _createElementBlock("tr", _hoisted_15, [
                  _createElementVNode("td", null, _toDisplayString(`${_unref(t)('general__text__created')} ${_unref(t)('general__text__by').toLowerCase()}`), 1),
                  _createElementVNode("td", null, _toDisplayString(documentMetadata.value.creator), 1)
                ]))
              : _createCommentVNode("", true),
            (documentMetadata.value?.nrOfPages)
              ? (_openBlock(), _createElementBlock("tr", _hoisted_16, [
                  _createElementVNode("td", null, _toDisplayString(_unref(t)('general__text__pages-number-of')), 1),
                  _createElementVNode("td", null, _toDisplayString(documentMetadata.value.nrOfPages), 1)
                ]))
              : _createCommentVNode("", true),
            (imageMetadata.value?.width)
              ? (_openBlock(), _createElementBlock("tr", _hoisted_17, [
                  _createElementVNode("td", null, _toDisplayString(_unref(t)('general__text__width')), 1),
                  _createElementVNode("td", null, _toDisplayString(imageMetadata.value?.width), 1)
                ]))
              : _createCommentVNode("", true),
            (imageMetadata.value?.height)
              ? (_openBlock(), _createElementBlock("tr", _hoisted_18, [
                  _createElementVNode("td", null, _toDisplayString(_unref(t)('general__text__height')), 1),
                  _createElementVNode("td", null, _toDisplayString(imageMetadata.value?.height), 1)
                ]))
              : _createCommentVNode("", true),
            (imageMetadata.value?.xresolution)
              ? (_openBlock(), _createElementBlock("tr", _hoisted_19, [
                  _createElementVNode("td", null, _toDisplayString(_unref(t)('general__text__resolution-horizontal')), 1),
                  _createElementVNode("td", null, _toDisplayString(imageMetadata.value?.xresolution), 1)
                ]))
              : _createCommentVNode("", true),
            (imageMetadata.value?.xResolution)
              ? (_openBlock(), _createElementBlock("tr", _hoisted_20, [
                  _createElementVNode("td", null, _toDisplayString(_unref(t)('general__text__resolution-horizontal')), 1),
                  _createElementVNode("td", null, _toDisplayString(imageMetadata.value?.xResolution), 1)
                ]))
              : _createCommentVNode("", true),
            (imageMetadata.value?.yresolution)
              ? (_openBlock(), _createElementBlock("tr", _hoisted_21, [
                  _createElementVNode("td", null, _toDisplayString(_unref(t)('general__text__resolution-vertical')), 1),
                  _createElementVNode("td", null, _toDisplayString(imageMetadata.value?.yresolution), 1)
                ]))
              : _createCommentVNode("", true),
            (imageMetadata.value?.yResolution)
              ? (_openBlock(), _createElementBlock("tr", _hoisted_22, [
                  _createElementVNode("td", null, _toDisplayString(_unref(t)('general__text__resolution-vertical')), 1),
                  _createElementVNode("td", null, _toDisplayString(imageMetadata.value?.yResolution), 1)
                ]))
              : _createCommentVNode("", true),
            (imageMetadata.value?.lastModified)
              ? (_openBlock(), _createElementBlock("tr", _hoisted_23, [
                  _createElementVNode("td", null, _toDisplayString(_unref(t)('general__text__last-modified-date')), 1),
                  _createElementVNode("td", null, _toDisplayString(new Date(imageMetadata.value.lastModified).toLocaleString()), 1)
                ]))
              : _createCommentVNode("", true)
          ])
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("footer", _hoisted_24, [
      _createVNode(TooltipStandardV2, {
        text: _unref(t)('general__text__download'),
        arrowPosition: 'bottom-center',
        verticalOffset: 2
      }, {
        default: _withCtx(() => [
          _createVNode(ButtonOutline, {
            class: "button",
            iconLeft: require('@/design-system/assets/icons/Icon-Download-small.svg'),
            size: 'small',
            onOnClicked: _cache[2] || (_cache[2] = ($event: any) => (emits('downloadFile')))
          }, null, 8, ["iconLeft"])
        ]),
        _: 1
      }, 8, ["text"]),
      (_unref(store).getters.hasFileEditorAccess)
        ? (_openBlock(), _createBlock(TooltipStandardV2, {
            key: 0,
            text: _unref(t)('general__text__archive'),
            arrowPosition: 'bottom-center',
            verticalOffset: 2
          }, {
            default: _withCtx(() => [
              _createVNode(ButtonOutline, {
                class: "button",
                iconLeft: require('@/design-system/assets/icons/Icon-Archive-Small.svg'),
                size: 'small',
                onOnClicked: _cache[3] || (_cache[3] = ($event: any) => (emits('archiveFile')))
              }, null, 8, ["iconLeft"])
            ]),
            _: 1
          }, 8, ["text"]))
        : _createCommentVNode("", true),
      (_unref(store).getters.hasFileAdminAccess)
        ? (_openBlock(), _createBlock(TooltipStandardV2, {
            key: 1,
            text: _unref(t)('general__text__delete'),
            arrowPosition: 'bottom-center',
            verticalOffset: 2
          }, {
            default: _withCtx(() => [
              _createVNode(ButtonOutline, {
                class: "button",
                iconLeft: require('@/design-system/assets/icons/Icon-Trash-2-small.svg'),
                size: 'small',
                onOnClicked: _cache[4] || (_cache[4] = ($event: any) => (emits('deleteFile')))
              }, null, 8, ["iconLeft"])
            ]),
            _: 1
          }, 8, ["text"]))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})